import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import {
	graphql,
	type PreloadedQuery,
	useRefetchableFragment,
	usePreloadedQuery,
} from 'react-relay';
import { Inline, xcss } from '@atlaskit/primitives';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import FilterRefinement from '@atlassian/jira-filter-refinement/src/ui/index.tsx';
import {
	fireOperationalAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { boardFilterRefinementQuery } from '@atlassian/jira-relay/src/__generated__/boardFilterRefinementQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useSuspenselessRefetch } from '@atlassian/jira-issue-hooks/src/services/use-suspenseless-refetch/index.tsx';
import { CLEAR } from '@atlassian/jira-software-filters/src/common/constants.tsx';
import CustomFilterRelay from '@atlassian/jira-software-filters/src/ui/custom-filter/index.tsx';
import type { CustomFilterId } from '@atlassian/jira-filters/src/common/types.tsx';
import ClearButton from '@atlassian/jira-filters/src/ui/filters/clear-button/index.tsx';
import boardFilterRefinementRefetchQuery, {
	type boardFilterRefinementRefetchQuery as boardFilterRefinementRefetchQueryType,
} from '@atlassian/jira-relay/src/__generated__/boardFilterRefinementRefetchQuery.graphql';
import type { boardFilterRefinement_softwareBoard$key } from '@atlassian/jira-relay/src/__generated__/boardFilterRefinement_softwareBoard.graphql';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/index.tsx';
import { getErrorHash } from '@atlassian/jira-errors-handling/src/utils/error-hash.tsx';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import { getIsSoftwareThemingEnabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { ThemedButtonWrapper } from '@atlassian/jira-project-theme-components/src/ui/themed-button-new/ThemedButtonNew.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { useBoardDispatch, useBoardSelector } from '../../../../state/index.tsx';
import { setAllFilters } from '../../../../state/actions/filter/index.tsx';
import { isCustomFiltersActive } from '../../../../state/selectors/filter/custom-filter-selectors.tsx';
import {
	getJqlFilter,
	getSelectedCustomFilters,
	getWorkFilters,
	isBoardConfigLoaded,
	isJqlFilterActive,
	orderedWorkAssigneesByAccountIdSelector,
	hasAnyActiveOptimistic,
} from '../../../../state/selectors/work/work-selectors.tsx';
import {
	getCanInviteOthersToProject,
	projectIdSelector,
	projectKeySelector,
	projectNameSelector,
	rapidViewIdSelector,
} from '../../../../state/selectors/software/software-selectors.tsx';
import { CUSTOM_FILTER, JQL, type Filters } from '../../../../model/filter/filter-types.tsx';
import { getSwimlaneMode } from '../../../../state/selectors/swimlane/swimlane-mode-selectors.tsx';
import { swimlaneModeToStrategiesFilterRefinement } from '../../../../services/swimlane/swimlane-data-transformer.tsx';
import { getPermissionsSelector } from '../../../../state/selectors/board/board-permissions-selectors.tsx';
import { CAN_EDIT_BOARD } from '../../../../model/permission/permission-types.tsx';
import messages from './messages.tsx';

type Props = {
	boardFilterRefinementQueryRef: PreloadedQuery<boardFilterRefinementQuery>;
	isActiveStandup?: boolean;
};

const CHANGEBOARDING_MESSAGE_ID = 'jira-filter-refinement-for-tmp-boards';

const useAddPeopleButtonProps = () => {
	const canInviteOthersToProject = useBoardSelector(getCanInviteOthersToProject);
	const permissions = useBoardSelector(getPermissionsSelector);
	const projectId = useBoardSelector(projectIdSelector);
	const projectKey = useBoardSelector(projectKeySelector);
	const projectName = useBoardSelector(projectNameSelector);

	return useMemo(() => {
		const isAddPeopleButtonEnabled =
			(permissions[CAN_EDIT_BOARD] ||
				(canInviteOthersToProject &&
					expValEquals('open_invite_for_open_tmp_projects', 'cohort', 'experiment'))) &&
			!isVisualRefreshEnabled();

		return {
			isAddPeopleButtonEnabled,
			projectId,
			projectKey,
			projectName,
		};
	}, [canInviteOthersToProject, permissions, projectId, projectKey, projectName]);
};

export const BoardFilterRefinement = ({
	boardFilterRefinementQueryRef,
	isActiveStandup,
}: Props) => {
	const { formatMessage } = useIntl();

	const cloudId = useCloudId();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isSoftwareFullTheming } =
		// eslint-disable-next-line jira/ff/no-preconditioning -- we are rolling out the fast follows after the experiment has started
		fg('jira_theming_milestone_1_fast_follows') && getIsSoftwareThemingEnabled()
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useSoftwareProjectTheming()
			: { isSoftwareFullTheming: false };

	const dispatch = useBoardDispatch();

	const assignees = useBoardSelector(orderedWorkAssigneesByAccountIdSelector);
	const isBoardLoaded = useBoardSelector(isBoardConfigLoaded);
	const boardId = useBoardSelector(rapidViewIdSelector);
	const jql = useBoardSelector(getJqlFilter);
	const hasActiveOptimistic = useBoardSelector(hasAnyActiveOptimistic);

	const jqlFilterActive = useBoardSelector(isJqlFilterActive);
	const selectedCustomFilters = useBoardSelector(getSelectedCustomFilters);
	const customFiltersActive = useBoardSelector(isCustomFiltersActive);
	const swimlaneMode = useBoardSelector(getSwimlaneMode);
	const workFilters = useBoardSelector(getWorkFilters);

	const boardFilterRefinementQueryData = usePreloadedQuery(
		graphql`
			query boardFilterRefinementQuery(
				$cloudId: ID!
				$boardId: ID!
				$filterRefinementScope: JiraJqlScopeInput!
			) @preloadable {
				...boardFilterRefinement_softwareBoard
					@arguments(cloudId: $cloudId, scope: $filterRefinementScope)
				...customFilter_softwareFilters_CustomFilterRelay @arguments(boardId: $boardId)
			}
		`,
		boardFilterRefinementQueryRef,
	);

	const [filterRefinementFragment, refetch] = useRefetchableFragment<
		boardFilterRefinementRefetchQueryType,
		boardFilterRefinement_softwareBoard$key
	>(
		graphql`
			fragment boardFilterRefinement_softwareBoard on Query
			@argumentDefinitions(cloudId: { type: "ID!" }, scope: { type: "JiraJqlScopeInput" })
			@refetchable(queryName: "boardFilterRefinementRefetchQuery") {
				...ui_filterRefinement_FilterRefinement @arguments(cloudId: $cloudId, scope: $scope)
			}
		`,
		boardFilterRefinementQueryData,
	);

	const scope = useMemo(
		() => ({
			board: {
				boardId: Number(boardId),
				swimlaneStrategy: swimlaneMode && swimlaneModeToStrategiesFilterRefinement[swimlaneMode],
			},
		}),
		[boardId, swimlaneMode],
	);

	const [suspenselessRefetch] = useSuspenselessRefetch(boardFilterRefinementRefetchQuery, refetch, {
		fetchPolicy: 'network-only',
	});

	/* eslint-disable react-hooks/rules-of-hooks */
	if (fg('update_assingee_after_inline_edit_fix')) {
		const previousAssignees = useRef(assignees);

		// When we detect a change in board issue assignees after initial load, we perform a refetch to get the latest values
		// We wait till board doesn't have active optimistic updates in flight
		useEffect(() => {
			if (isBoardLoaded && !hasActiveOptimistic && !isEqual(assignees, previousAssignees.current)) {
				previousAssignees.current = assignees;
				suspenselessRefetch({ cloudId, scope });
			}
		}, [
			assignees,
			cloudId,
			hasActiveOptimistic,
			isBoardLoaded,
			previousAssignees,
			scope,
			suspenselessRefetch,
		]);
	} else {
		const previousAssignees = usePrevious(assignees);

		// When we detect a change in board issue assignees after initial load, we perform a refetch to get the latest values
		useEffect(() => {
			if (isBoardLoaded && !isEqual(assignees, previousAssignees)) {
				suspenselessRefetch({ cloudId, scope });
			}
		}, [assignees, cloudId, isBoardLoaded, previousAssignees, scope, suspenselessRefetch]);
	}
	/* eslint-enable react-hooks/rules-of-hooks */

	const fireFilterBarChangedEvent = useCallback(
		(filterType: string) => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'changed',
				actionSubject: 'filterBar',
			});

			fireUIAnalytics(analyticsEvent, 'changeFilterBar', {
				filterType,
			});
		},
		[createAnalyticsEvent],
	);

	const onSearch = useCallback(
		(newJql: string) => {
			const newFilters = {
				// Remove all workFilters if jql is empty
				...(newJql.trim() !== '' ? workFilters.values : {}),
				[CUSTOM_FILTER]: selectedCustomFilters,
				[JQL]: newJql,
			};

			dispatch(setAllFilters(newFilters, JQL));

			fireFilterBarChangedEvent(JQL);
		},
		[dispatch, fireFilterBarChangedEvent, selectedCustomFilters, workFilters.values],
	);

	const onCustomFilterChange = useCallback(
		(customFilters: CustomFilterId[]) => {
			const newFilters: Filters = {
				...workFilters.values,
				[CUSTOM_FILTER]: customFilters,
				[JQL]: jql,
			};

			dispatch(setAllFilters(newFilters, CUSTOM_FILTER));

			fireFilterBarChangedEvent(CUSTOM_FILTER);
		},
		[dispatch, fireFilterBarChangedEvent, jql, workFilters.values],
	);

	const onClear = useCallback(() => {
		dispatch(setAllFilters({}, JQL));

		fireFilterBarChangedEvent(CLEAR);
	}, [dispatch, fireFilterBarChangedEvent]);

	const hasFiltersApplied = jqlFilterActive || customFiltersActive;

	const addPeopleButtonProps = useAddPeopleButtonProps();

	const onError = (_: string, error: Error) => {
		fireOperationalAnalytics(createAnalyticsEvent({}), 'ui taskFail', {
			screenName: 'board',
			task: 'loadCustomFilters',
			isNetworkError: isClientFetchError(error),
			hash: getErrorHash(error) ?? '',
			errorMessage: error.message,
		});
	};

	return (
		<Inline
			alignBlock="center"
			space={fg('filter-refinement-board-fix-button-spacing') ? 'space.100' : 'space.075'}
			shouldWrap
			xcss={isSoftwareFullTheming && themedStyling}
		>
			{isSoftwareFullTheming ? (
				<ThemedButtonWrapper>
					<FilterRefinement
						debounce={0} // board already applies a 500ms debounce on filter changes (SET_ALL_FILTERS action)
						defaultJql={jql || ''}
						onSearch={onSearch}
						fragmentRef={filterRefinementFragment}
						searchFieldPlaceholder={formatMessage(messages.filterRefinementSearchFieldPlaceholder)}
						scope={scope}
						changeboardingMessageId={CHANGEBOARDING_MESSAGE_ID}
						showAssigneeFilter={!isActiveStandup}
						{...addPeopleButtonProps}
						isSoftwareFullTheming={isSoftwareFullTheming}
					/>
				</ThemedButtonWrapper>
			) : (
				<FilterRefinement
					debounce={0} // board already applies a 500ms debounce on filter changes (SET_ALL_FILTERS action)
					defaultJql={jql || ''}
					onSearch={onSearch}
					fragmentRef={filterRefinementFragment}
					searchFieldPlaceholder={formatMessage(messages.filterRefinementSearchFieldPlaceholder)}
					scope={scope}
					changeboardingMessageId={CHANGEBOARDING_MESSAGE_ID}
					showAssigneeFilter={!isActiveStandup}
					{...addPeopleButtonProps}
				/>
			)}

			<JSErrorBoundary
				id="custom-filter-dropdown"
				packageName="software-filters"
				teamName="a4t-tanuki"
				fallback="flag"
				extraEventData={{ screenName: 'board' }}
				onError={onError}
			>
				<CustomFilterRelay
					screenName="board"
					onChange={onCustomFilterChange}
					selectedFilters={selectedCustomFilters}
					customFiltersFragmentRef={boardFilterRefinementQueryData}
					removeOuterPadding
				/>
			</JSErrorBoundary>
			{hasFiltersApplied && <ClearButton onClick={onClear} />}
		</Inline>
	);
};

const themedStyling = xcss({
	// @ts-expect-error --ds-background-input does not exist as a key
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/design-system/no-unsafe-design-token-usage
	'--ds-background-input': Tokens.COLOR_BACKGROUND_INPUT,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/design-system/no-unsafe-design-token-usage
	'--ds-background-input-pressed': Tokens.COLOR_BACKGROUND_INPUT,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/design-system/no-unsafe-design-token-usage
	'--ds-background-input-hovered': Tokens.COLOR_BACKGROUND_INPUT,
});
